import React, {useState, useEffect, useRef} from 'react';
import {
    Card,
    Form,
    Input,
    Button,
    Row,
    Col,
    InputNumber,
    Select,
    message,
    Space, Tooltip,
} from 'antd';
import {LinkOutlined, SaveOutlined, UndoOutlined} from '@ant-design/icons';
import {baseDataService, membershipService} from '../../../services';
import {ChineseMasterTabProps, DropdownResponse, MemberUpdateItem, MemGetMemberResponse} from "../../../model";
import SmartFormItem from "../../../components/common/SmartFormItem";
import EmailFormItem from "../../../components/common/EmailFormItem";
import dayjs from "dayjs";
import RocDateInputPicker from "../../../components/shared/RocDateInputPicker";

const {Option} = Select;

const ChineseMasterTab: React.FC<ChineseMasterTabProps> = ({
                                                               memberData,
                                                               onDataChanged,
                                                               changedFields,
                                                               onSaveSuccess,
                                                               isEditing,
                                                           }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [memberRegions, setMemberRegions] = useState<DropdownResponse[] | null>([]);
    const [memberTypes, setMemberTypes] = useState<DropdownResponse[] | null>([]);
    const [memberLevels, setMemberLevels] = useState<DropdownResponse[] | null>([]);
    const [mailDelivery, setMailDelivery] = useState<DropdownResponse[] | null>([]);
    const [fetchingMemberId, setFetchingMemberId] = useState(false);
    const [memberIdError, setMemberIdError] = useState<string | null>(null);
    const website = Form.useWatch('website', form); // 監聽 'website' 欄位
    const userAccount = localStorage.getItem('userAccount');
    
    // 保存原始的會員區域和類型值，用於比較
    const originalMemberRegionRef = useRef<string | null>(null);
    const originalMemberTypeRef = useRef<string | null>(null);
    
    // 防抖計時器
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // 載入下拉選單資料
    useEffect(() => {
        const fetchDropdownData = async () => {
            // 載入會員區域
            const regionData = await baseDataService.getDropdown({paramType: 'member_region'});
            setMemberRegions(regionData);

            // 載入會員類型
            const typeData = await baseDataService.getDropdown({paramType: 'member_type'});
            setMemberTypes(typeData);

            // 載入會員級數
            const levelData = await baseDataService.getDropdown({paramType: 'member_level'});
            setMemberLevels(levelData);

            // 載入會員級數
            const mailDelivery = await baseDataService.getDropdown({paramType: 'mail_delivery'});
            setMailDelivery(mailDelivery);
        };

        fetchDropdownData();
    }, []);


    // 初始化表單值
    useEffect(() => {
        form.setFieldsValue({
            ...memberData,
            joinDate: memberData.joinDate ? dayjs(memberData.joinDate) : null,
            establishDate: memberData.establishDate ? dayjs(memberData.establishDate) : null,
            suspensionDate: memberData.suspensionDate ? dayjs(memberData.suspensionDate, 'YYYY-MM-DD') : null,
            withdrawalDate: memberData.withdrawalDate ? dayjs(memberData.withdrawalDate) : null,
            modifiedDate: memberData.modifiedAt ? dayjs(memberData.modifiedAt) : null,
        });
        
        // 保存原始的會員區域和類型值
        originalMemberRegionRef.current = memberData.memberRegion;
        originalMemberTypeRef.current = memberData.memberType;
    }, [memberData, form]);
    
    // 約束函數，在組件卸載時清除計時器
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // 處理表單欄位變更
    const handleFieldsChange = (changedFields: any, allFields: any) => {
        if (!isEditing) return;

        let memberRegionChanged = false;
        let memberTypeChanged = false;
        
        // 確認每個有變更的欄位
        changedFields.forEach((field: any) => {
            const fieldName = field.name[0];
            const fieldValue = field.value;

            // 檢查是否為 memberRegion 或 memberType 變更
            if (fieldName === 'memberRegion') {
                memberRegionChanged = true;
            } else if (fieldName === 'memberType') {
                memberTypeChanged = true;
            }
            
            // 將日期轉為字串格式來比較
            let originalValue;
            // if (fieldName === 'joinDate' || fieldName === 'establishDate' || fieldName === 'suspensionDate' || fieldName === 'withdrawalDate' || fieldName === 'modifiedDate') {
            if (['joinDate' , 'establishDate' , 'suspensionDate' , 'withdrawalDate' , 'modifiedDate'].includes(fieldName)) {
                // For modifiedDate field, we need to check against modifiedAt from memberData
                const sourceField = fieldName === 'modifiedDate' ? 'modifiedAt' : fieldName;
                originalValue = memberData[sourceField as keyof MemGetMemberResponse]
                    ? dayjs(memberData[sourceField as keyof MemGetMemberResponse] as string).format('YYYY-MM-DD')
                    : null;
                // 檢查 fieldValue 是否為 moment 物件，並確認是否有效的日期
                const newValue = fieldValue && dayjs.isDayjs(fieldValue) && fieldValue.isValid()
                    ? fieldValue.format('YYYY-MM-DD')
                    : null;

                onDataChanged(fieldName, originalValue !== newValue);
            } else {
                originalValue = memberData[fieldName as keyof MemGetMemberResponse];

                // 處理 undefined 和 null
                onDataChanged(fieldName,
                    (originalValue === null || originalValue === undefined)
                        ? !!fieldValue
                        : fieldValue !== originalValue
                );
            }
        });
        
        // 如果 memberRegion 或 memberType 有變更，需要獲取新的 memberId
        if (memberRegionChanged || memberTypeChanged) {
            handleGetNextMemberId();
        }
    };
    
    // 獲取新的 memberId
    const handleGetNextMemberId = () => {
        // 清除之前的計時器
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        // 設置防抖計時器，500ms 後執行
        debounceTimerRef.current = setTimeout(async () => {
            const currentValues = form.getFieldsValue();
            const currentMemberRegion = currentValues.memberRegion;
            const currentMemberType = currentValues.memberType;
            
            // 檢查 memberRegion 和 memberType 是否都有值
            if (!currentMemberRegion || !currentMemberType) {
                return;
            }
            
            // 檢查是否與原始值相同
            const isRegionSameAsOriginal = currentMemberRegion === originalMemberRegionRef.current;
            const isTypeSameAsOriginal = currentMemberType === originalMemberTypeRef.current;
            
            // 如果兩個值都與原始值相同，不需要調用 API
            if (isRegionSameAsOriginal && isTypeSameAsOriginal) {
                // 將 memberId 重置為原始值
                form.setFieldsValue({
                    memberId: memberData.memberId
                });
                return;
            }
            
            try {
                // 設置載入狀態
                setFetchingMemberId(true);
                setMemberIdError(null);
                
                // 調用 API 獲取新的 memberId
                const response = await baseDataService.getNextMemberIdPreview({
                    memberRegion: currentMemberRegion,
                    memberType: currentMemberType
                });
                
                // 如果成功獲取新的 memberId，更新表單
                if (response) {
                    form.setFieldsValue({
                        memberId: response.memberId
                    });
                    
                    // 標記 memberId 欄位為已更改
                    onDataChanged('memberId', true);
                }
            } catch (error) {
                console.error('Failed to get next member ID:', error);
                setMemberIdError('無法獲取新的會員編號，請稍後再試');
                message.error('無法獲取新的會員編號，請稍後再試');
            } finally {
                setFetchingMemberId(false);
            }
        }, 500);
    };

    // 儲存變更
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // 如果沒有變更欄位，直接返回
            if (changedFields.size === 0) {
                message.info('沒有需要儲存的變更');
                return;
            }

            setLoading(true);

            // 檢查會員編號是否有變更
            const isMemberIdChanged = changedFields.has('memberId');
            
            // 準備更新資料
            const updateData: MemberUpdateItem = {
                membershipId: memberData.membershipId,
                memberId: values.memberId,
                companyName: values.companyName,
                contactZip: values.contactZip,
                contactAddress: values.contactAddress,
                companyPhone: values.companyPhone,
                companyFax: values.companyFax,
                factoryZip: values.factoryZip,
                factoryAddress: values.factoryAddress,
                factoryPhone: values.factoryPhone,
                factoryFax: values.factoryFax,
                chairman: values.chairman,
                generalManager: values.generalManager,
                generalManagerPhone: values.generalManagerPhone,
                generalManagerEmail: values.generalManagerEmail,
                responsiblePerson: values.responsiblePerson,
                responsiblePersonPhone: values.responsiblePersonPhone,
                responsiblePersonEmail: values.responsiblePersonEmail,
                membershipLevel: values.membershipLevel,
                joinDate: values.joinDate ? values.joinDate.format('YYYY-MM-DD') : undefined,
                establishDate: values.establishDate ? values.establishDate.format('YYYY-MM-DD') : undefined,
                capitalAmount: values.capitalAmount,
                employeeCount: values.employeeCount,
                powerUsage: values.powerUsage,
                factoryRegistrationNo: values.factoryRegistrationNo,
                businessId: values.businessId,
                email: values.email,
                website: values.website,
                mailDelivery: values.mailDelivery,
                omemberId: memberData.memberId,
                suspensionDate: values.suspensionDate ? values.suspensionDate.format('YYYY-MM-DD') : undefined,
                suspensionReason: values.suspensionReason,
                withdrawalDate: values.withdrawalDate ? values.withdrawalDate.format('YYYY-MM-DD') : undefined,
                withdrawalReason: values.withdrawalReason,
                modifiedBy: userAccount ?? '',
                collectionAgent: values.collectionAgent
            };

            // 如果會員編號有變更，需要先調用更新會員編號序列 API
            if (isMemberIdChanged) {
                try {
                    // 從 memberId 中提取 memberType 和 memberRegion
                    // 假設格式為：type + region + serial，例如「A01234」中的「A」為 type，「0」為 region
                    const memberId = values.memberId;
                    const memberType = values.memberType;
                    const memberRegion = values.memberRegion;
                    
                    // 一般會員格式為「區域 + 4 碼序號」，贊助會員格式為「A + 區域 + 3 碼序號」。
                    const serialStartIndex = String(memberType).toUpperCase() === 'A' ? 2 : 1;
                    const serialPart = memberId.substring(serialStartIndex);
                    
                    // 調用 API 更新序列號
                    const serialUpdateResponse = await baseDataService.updateMemberIdSerial({
                        memberType,
                        memberRegion,
                        paramValue: serialPart
                    });
                    
                    // 如果更新序列號失敗，直接返回錯誤
                    if (!serialUpdateResponse || !serialUpdateResponse.success) {
                        message.error('會員編號序列更新失敗，無法儲存資料');
                        setLoading(false);
                        return;
                    }
                } catch (error) {
                    console.error('Update member ID serial failed:', error);
                    message.error('會員編號序列更新時發生錯誤，無法儲存資料');
                    setLoading(false);
                    return;
                }
            }

            // 繼續進行會員資料更新
            const response = await membershipService.updateMember(
                {
                    members: [
                        {
                            ...memberData,
                            ...updateData
                        }]
                }
            );

            if (response && response.updatedCount > 0) {
                message.success('會員資料已更新成功');
            // 觸發會員數量更新事件
                window.dispatchEvent(new Event('memberCountUpdate'));
                onSaveSuccess();
            } else if (response) {
                message.error('會員資料更新失敗');
            }
        } catch (error) {
            console.error('Update member failed:', error);
            message.error('會員資料更新發生錯誤');
        } finally {
            setLoading(false);
        }
    };

    // 重置表單
    const handleReset = () => {

        form.setFieldsValue({
            ...memberData,
            joinDate: memberData.joinDate ? dayjs(memberData.joinDate) : null,
            establishDate: memberData.establishDate ? dayjs(memberData.establishDate) : null,
            suspensionDate: memberData.suspensionDate ? dayjs(memberData.suspensionDate, 'YYYY-MM-DD') : null,
            withdrawalDate: memberData.withdrawalDate ? dayjs(memberData.withdrawalDate) : null,
            modifiedDate: memberData.modifiedAt ? dayjs(memberData.modifiedAt) : null,
        });

        // 清除所有變更標記
        changedFields.forEach(field => {
            onDataChanged(field, false);
        });
    };

    // 判斷欄位是否有變更
    const isFieldChanged = (fieldName: string) => {
        return changedFields.has(fieldName);
    };

    const multiEmailRule = {
        validator: async (_: any, value?: string) => {
            if (!value) return Promise.resolve();

            // 允許逗號/分號/換行分隔
            const emails = value
            .split(/[,;\n]/)
            .map(s => s.trim())
            .filter(Boolean);

            // 允許只輸入分隔符號/空白時視為空
            if (emails.length === 0) return Promise.resolve();

            // 使用簡單但夠用的 email regex（不要太嚴格）
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            const invalid = emails.filter(e => !emailRegex.test(e));
            if (invalid.length > 0) {
            return Promise.reject(
                new Error(`Email 格式錯誤：${invalid.join(', ')}`)
            );
            }
            return Promise.resolve();
        },
    };

    return (
        <Card>
            <Form
                form={form}
                layout="horizontal"
                labelCol={{span: 6}}
                wrapperCol={{span: 18}}
                onFieldsChange={handleFieldsChange}
                initialValues={{
                    ...memberData,
                    joinDate: memberData.joinDate ? dayjs(memberData.joinDate) : null,
                    establishDate: memberData.establishDate ? dayjs(memberData.establishDate) : null,
                    suspensionDate: memberData.suspensionDate ? dayjs(memberData.suspensionDate, 'YYYY-MM-DD') : null,
                    withdrawalDate: memberData.withdrawalDate ? dayjs(memberData.withdrawalDate) : null,
                    modifiedDate: memberData.modifiedAt ? dayjs(memberData.modifiedAt) : null,
                }}
            >
                <Row gutter={[24, 16]}>
                    {/* 第一行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="會員證號"
                            validateStatus={memberIdError ? 'error' : (isFieldChanged('memberId') ? 'warning' : '')}
                            help={memberIdError || (isFieldChanged('memberId') ? '此欄位已修改' : '')}
                        >
                            <Input.Group compact>
                                <Form.Item name="memberType" noStyle>
                                    <Select
                                        style={{width: '30%'}}
                                        disabled={!isEditing}
                                        placeholder="類型"
                                        onBlur={handleGetNextMemberId}
                                    >
                                        {memberTypes?.map(type => (
                                            <Option key={type.paramCode} value={type.paramCode}>
                                                {type.paramValue}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item name="memberRegion" noStyle>
                                    <Select
                                        style={{width: '30%'}}
                                        disabled={!isEditing}
                                        placeholder="區域"
                                        onBlur={handleGetNextMemberId}
                                    >
                                        {memberRegions?.map(region => (
                                            <Option key={region.paramCode} value={region.paramCode}>
                                                {region.paramValue}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item name="memberId" noStyle>
                                    <Input
                                        style={{width: '40%'}}
                                        disabled
                                        placeholder="編號"
                                        suffix={fetchingMemberId ? <span style={{color: '#1890ff'}}>載入中...</span> : null}
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}></Col>
                </Row>
                <Row gutter={[24, 16]}>
                    {/* 第二行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="公司名稱"
                            name="companyName"
                            validateStatus={isFieldChanged('companyName') ? 'warning' : ''}
                            help={isFieldChanged('companyName') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="公司或聯絡住址"
                            validateStatus={isFieldChanged('contactAddress') || isFieldChanged('contactZip') ? 'warning' : ''}
                            help={isFieldChanged('contactAddress') || isFieldChanged('contactZip') ? '此欄位已修改' : ''}
                        >
                            <Input.Group compact>
                                <Form.Item name="contactZip" noStyle>
                                    <Input
                                        style={{width: '20%'}}
                                        placeholder="郵遞區號"
                                        disabled={!isEditing}
                                    />
                                </Form.Item>
                                <Form.Item name="contactAddress" noStyle>
                                    <Input
                                        style={{width: '80%'}}
                                        placeholder="地址"
                                        disabled={!isEditing}
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第三行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <SmartFormItem
                            label="公司電話"
                            name="companyPhone"
                            rules={[
                                {
                                    pattern: /^[0-9+() ~#-]*$/,
                                    message: '只能輸入數字與符號',
                                },
                            ]}
                            isFieldChanged={isFieldChanged}
                        >
                            <Input
                                disabled={!isEditing}
                            />
                        </SmartFormItem>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <SmartFormItem
                            label="公司傳真"
                            name="companyFax"
                            rules={[
                                {
                                    pattern: /^[0-9+() ~#-]*$/,
                                    message: '只能輸入數字與符號',
                                },
                            ]}
                            isFieldChanged={isFieldChanged}
                            // validateStatus={isFieldChanged('companyFax') ? 'warning' : ''}
                            // help={isFieldChanged('companyFax') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </SmartFormItem>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第四行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="工廠地址"
                            validateStatus={isFieldChanged('factoryAddress') || isFieldChanged('factoryZip') ? 'warning' : ''}
                            help={isFieldChanged('factoryAddress') || isFieldChanged('factoryZip') ? '此欄位已修改' : ''}
                        >
                            <Input.Group compact>
                                <Form.Item name="factoryZip" noStyle>
                                    <Input
                                        style={{width: '20%'}}
                                        placeholder="郵遞區號"
                                        disabled={!isEditing}
                                    />
                                </Form.Item>
                                <Form.Item name="factoryAddress" noStyle>
                                    <Input
                                        style={{width: '80%'}}
                                        placeholder="地址"
                                        disabled={!isEditing}
                                    />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <SmartFormItem
                            label="工廠電話"
                            name="factoryPhone"
                            rules={[
                                {
                                    pattern: /^[0-9+() ~#-]*$/,
                                    message: '只能輸入數字與符號',
                                },
                            ]}
                            isFieldChanged={isFieldChanged}
                            // validateStatus={isFieldChanged('factoryPhone') ? 'warning' : ''}
                            // help={isFieldChanged('factoryPhone') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </SmartFormItem>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第五行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <SmartFormItem
                            label="工廠傳真"
                            name="factoryFax"
                            rules={[
                                {
                                    pattern: /^[0-9+() ~#-]*$/,
                                    message: '只能輸入數字與符號',
                                },
                            ]}
                            isFieldChanged={isFieldChanged}
                        >
                            <Input disabled={!isEditing}/>
                        </SmartFormItem>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="董事長"
                            name="chairman"
                            validateStatus={isFieldChanged('chairman') ? 'warning' : ''}
                            help={isFieldChanged('chairman') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第六行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="總經理"
                            name="generalManager"
                            validateStatus={isFieldChanged('generalManager') ? 'warning' : ''}
                            help={isFieldChanged('generalManager') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <SmartFormItem
                            label="總經理手機"
                            name="generalManagerPhone"
                            rules={[
                                {
                                    pattern: /^[0-9+() ~#-]*$/,
                                    message: '只能輸入數字與符號',
                                },
                            ]}
                            isFieldChanged={isFieldChanged}
                        >
                            <Input disabled={!isEditing}/>
                        </SmartFormItem>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第七行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <EmailFormItem
                            label="總經理e-mail"
                            name="generalManagerEmail"
                            rules={[multiEmailRule]}
                            isFieldChanged={isFieldChanged}
                            // validateStatus={isFieldChanged('generalManagerEmail') ? 'warning' : ''}
                            // help={isFieldChanged('generalManagerEmail') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </EmailFormItem>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第八行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="負責人"
                            name="responsiblePerson"
                            validateStatus={isFieldChanged('responsiblePerson') ? 'warning' : ''}
                            help={isFieldChanged('responsiblePerson') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <SmartFormItem
                            label="負責人手機"
                            name="responsiblePersonPhone"
                            rules={[
                                {
                                    pattern: /^[0-9+() ~#-]*$/,
                                    message: '只能輸入數字與符號',
                                },
                            ]}
                            isFieldChanged={isFieldChanged}
                        >
                            <Input disabled={!isEditing}/>
                        </SmartFormItem>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第九行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <EmailFormItem
                            label="負責人e-mail"
                            name="responsiblePersonEmail"
                            rules={[multiEmailRule]}
                            isFieldChanged={isFieldChanged}                        >
                            <Input disabled={!isEditing}/>
                        </EmailFormItem>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="會員級數"
                            name="membershipLevel"
                            validateStatus={isFieldChanged('membershipLevel') ? 'warning' : ''}
                            help={isFieldChanged('membershipLevel') ? '此欄位已修改' : ''}
                        >
                            <Select disabled={!isEditing}>
                                {memberLevels?.map(level => (
                                    <Option key={level.paramCode} value={level.paramCode}>
                                        {level.paramValue}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="入會日期"
                            name="joinDate"
                            validateStatus={isFieldChanged('joinDate') ? 'warning' : ''}
                            help={isFieldChanged('joinDate') ? '此欄位已修改' : ''}
                        >
                            {/*<DatePicker locale={locale} style={{width: '100%'}} disabled={!isEditing}/>*/}
                            {/*<ROCDatePicker*/}
                            {/*    style={{width: '100%'}}*/}
                            {/*    picker="month"*/}
                            {/*    disabled={!isEditing}*/}
                            {/*/>*/}
                            <RocDateInputPicker
                                disabled={!isEditing}
                                mode="yyy-mm"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十一行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="創業日期"
                            name="establishDate"
                            validateStatus={isFieldChanged('establishDate') ? 'warning' : ''}
                            help={isFieldChanged('establishDate') ? '此欄位已修改' : ''}
                        >
                            {/*<ROCDatePicker*/}
                            {/*    style={{width: '100%'}}*/}
                            {/*    picker="month"*/}
                            {/*    disabled={!isEditing}*/}
                            {/*/>*/}
                            <RocDateInputPicker
                                disabled={!isEditing}
                                mode="yyy-mm"
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="資本額"
                            name="capitalAmount"
                            validateStatus={isFieldChanged('capitalAmount') ? 'warning' : ''}
                            help={isFieldChanged('capitalAmount') ? '此欄位已修改' : ''}
                        >
                            <InputNumber
                                style={{width: '100%'}}
                                disabled={!isEditing}
                                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={value => value!.replace(/\$\s?|(,*)/g, '')}
                                addonAfter="元"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十二行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="員工數"
                            name="employeeCount"
                            validateStatus={isFieldChanged('employeeCount') ? 'warning' : ''}
                            help={isFieldChanged('employeeCount') ? '此欄位已修改' : ''}
                        >
                            <InputNumber
                                style={{width: '100%'}}
                                disabled={!isEditing}
                                addonAfter="人"
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="使用動力"
                            name="powerUsage"
                            validateStatus={isFieldChanged('powerUsage') ? 'warning' : ''}
                            help={isFieldChanged('powerUsage') ? '此欄位已修改' : ''}
                        >
                            <InputNumber
                                style={{width: '100%'}}
                                disabled={!isEditing}
                                addonAfter="kW(瓩)"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十三行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="工廠登記字號"
                            name="factoryRegistrationNo"
                            validateStatus={isFieldChanged('factoryRegistrationNo') ? 'warning' : ''}
                            help={isFieldChanged('factoryRegistrationNo') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="統一編號"
                            name="businessId"
                            validateStatus={isFieldChanged('businessId') ? 'warning' : ''}
                            help={isFieldChanged('businessId') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十四行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <EmailFormItem
                            label="電子郵件"
                            name="email"
                            rules={[multiEmailRule]}
                            isFieldChanged={isFieldChanged}
                        >
                            <Input disabled={!isEditing}/>
                        </EmailFormItem>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="網址"
                            name="website"
                            validateStatus={isFieldChanged('website') ? 'warning' : ''}
                            help={isFieldChanged('website') ? '此欄位已修改' : ''}
                        >
                            <Input
                                disabled={!isEditing}
                                addonAfter={
                                    <Tooltip title="開啟網址">
                                        <a href={`http://${website}`} target="_blank" rel="noopener noreferrer">
                                            <LinkOutlined style={{ fontSize: '16px', color: '#1890ff' }}/>
                                        </a>
                                    </Tooltip>
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十五行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="信件投遞地點"
                            name="mailDelivery"
                            validateStatus={isFieldChanged('mailDelivery') ? 'warning' : ''}
                            help={isFieldChanged('mailDelivery') ? '此欄位已修改' : ''}
                        >
                            <Select disabled={!isEditing}>
                                {
                                    mailDelivery?.map(deliverty => (
                                        <Option key={deliverty.paramCode} value={deliverty.paramCode}>
                                            {deliverty.paramValue}
                                        </Option>
                                    ))
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="代收信件人"
                            name="collectionAgent"
                            validateStatus={isFieldChanged('collectionAgent') ? 'warning' : ''}
                            help={isFieldChanged('collectionAgent') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十六行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="停權日期"
                            name="suspensionDate"
                            validateStatus={isFieldChanged('suspensionDate') ? 'warning' : ''}
                            help={isFieldChanged('suspensionDate') ? '此欄位已修改' : ''}
                        >
                            {/*<DatePicker locale={locale} style={{width: '100%'}} disabled={!isEditing}/>*/}
                            {/*<ROCDatePicker*/}
                            {/*    style={{width: '100%'}}*/}
                            {/*    disabled={!isEditing}*/}
                            {/*/>*/}
                            <RocDateInputPicker
                                disabled={!isEditing}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="停權原因"
                            name="suspensionReason"
                            validateStatus={isFieldChanged('suspensionReason') ? 'warning' : ''}
                            help={isFieldChanged('suspensionReason') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十七行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="退會日期"
                            name="withdrawalDate"
                            validateStatus={isFieldChanged('withdrawalDate') ? 'warning' : ''}
                            help={isFieldChanged('withdrawalDate') ? '此欄位已修改' : ''}
                        >
                            {/*<DatePicker locale={locale} style={{width: '100%'}} disabled={!isEditing}/>*/}
                            {/*<ROCDatePicker*/}
                            {/*    style={{width: '100%'}}*/}
                            {/*    disabled={!isEditing}*/}
                            {/*/>*/}
                            <RocDateInputPicker
                                disabled={!isEditing}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="退會原因"
                            name="withdrawalReason"
                            validateStatus={isFieldChanged('withdrawalReason') ? 'warning' : ''}
                            help={isFieldChanged('withdrawalReason') ? '此欄位已修改' : ''}
                        >
                            <Input disabled={!isEditing}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                    {/* 第十八行 */}
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="異動人員"
                            name="modifiedBy"
                            validateStatus={isFieldChanged('modifiedBy') ? 'warning' : ''}
                            help={isFieldChanged('modifiedBy') ? '此欄位已修改' : ''}
                        >
                            {/*<Input disabled={!isEditing}/>*/}
                            <Input disabled/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
                        <Form.Item
                            label="異動日期"
                            name="modifiedDate"
                            validateStatus={isFieldChanged('modifiedDate') ? 'warning' : ''}
                            help={isFieldChanged('modifiedDate') ? '此欄位已修改' : ''}
                        >
                            {/*<DatePicker*/}
                            {/*    locale={locale} style={{width: '100%'}}*/}
                            {/*    disabled*/}
                            {/*/>*/}
                            {/*<ROCDatePicker*/}
                            {/*    style={{width: '100%'}}*/}
                            {/*    disabled*/}
                            {/*/>*/}
                            <RocDateInputPicker
                                disabled
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 16]}>

                </Row>

                {/* 按鈕區域 */}
                {isEditing && (
                    <Row justify="end" style={{marginTop: 24}}>
                        <Space>
                            <Button
                                icon={<UndoOutlined/>}
                                onClick={handleReset}
                                disabled={changedFields.size === 0}
                            >
                                重置
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined/>}
                                onClick={handleSave}
                                loading={loading}
                                disabled={changedFields.size === 0}
                            >
                                儲存
                            </Button>
                        </Space>
                    </Row>
                )}
            </Form>
        </Card>
    );
};

export default ChineseMasterTab;
